<template>
  <b-container fluid class="chat-form" :class="chatFormClass">
    <b-row no-gutters>
      <b-col cols="12" class="input">
        <b-form-group
          :id="`chat-form-group-${_uid}`"
          label="Enter a question"
          :label-for="`chat-form-textarea-${_uid}`"
          label-sr-only
        >
          <b-form-textarea ref="chatInput" :id="`chat-form-textarea-${_uid}`"
            v-model="textInput" :rows="2"
            :maxlength="maxInputLength"
            @focus.native="onFocus(true)"
            @blur.native="onFocus(false)"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row no-gutters class="action">
      <b-col class="text-right">
        <b-dropdown :id="`chat-form-dropdown-${_uid}`"
          variant="primary"
          right
          dropup
          split
          @click="send"
          :disabled="!textInput.trim()"
        >
          <template slot="button-content">
            <!-- <i class="togo-icon togo-icon-lg" :class="selectClass"></i> -->
            {{ selectLabel }}
          </template>

          <b-dropdown-item v-for="recipient in otherRecipients" :key="recipient.id"
            @click="selectRecipient = recipient">
            <i class="togo-icon togo-icon-lg" :class="recipientClass(recipient)"></i>
            {{ recipientLabel(recipient) }}
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
export const NO_RECIPIENT = {};

const ChatForm = {
  name: 'ChatForm',

  props: {
    recipients: {
      type: Object
    },
    chatEnabled: {
      type: Boolean,
      default: true
    },
    questionsEnabled: {
      type: Boolean,
      default: true
    },
    maxInputLength: {
      type: Number,
      default: 1024
    }
  },

  data() {
    return {
      textInput: '',
      selectRecipient: NO_RECIPIENT
    };
  },

  computed: {
    chatFormClass() {
      return {
        'no-other-recipients': Object.keys(this.otherRecipients).length === 0
      };
    },

    brandingStyle() {
      return this.$store.getters['g2wWebinar/brandingStyle'];
    },

    sendButtonStyle() {
      if (this.textInput) {
        return {
          backgroundColor: this.brandingStyle.headerColor,
          color: 'white'
        };
      }
      return {};
    },

    otherRecipients() {
      let others = {};

      if (this.chatEnabled) {
        others = Object.assign({}, this.recipients);
      }

      if (this.questionsEnabled) {
        others[NO_RECIPIENT.id] = NO_RECIPIENT;
      }

      if (this.selectRecipient) {
        delete others[this.selectRecipient.id];
      }

      return others;
    },

    selectClass() {
      return this.recipientClass(this.selectRecipient);
    },

    selectLabel() {
      return this.recipientLabel(this.selectRecipient) || this.$t('chat.send');
    }
  },

  watch: {
    questionsEnabled(newValue) {
      if (!newValue && this.selectRecipient === NO_RECIPIENT) {
        const firstOtherKey = Object.keys(this.otherRecipients)[0];
        this.selectRecipient = this.otherRecipients[firstOtherKey];
      }
    }
  },

  activated() {
    if (this.$refs.chatInput.value.length) {
      this.$refs.chatInput.focus();
    }
  },

  methods: {
    recipientClass(recipient) {
      if (recipient.id) {
        return 'togo-icon-person';
      }

      return 'togo-icon-chat-question';
    },

    recipientLabel(/* recipient */) {
      // if recipient input, could contain name and roles
      // ex: "Send to Foo Bar (Organizer)"
      return this.$t('chat.send');
    },

    send() {
      const textInput = this.textInput.slice(0, this.maxInputLength);

      if (this.selectRecipient.id) {
        const chatMessage = {
          text: textInput,
          recipientId: this.selectRecipient.id,
          recipientGroupId: 0
        };

        this.$emit('chat', {chatMessage});
      } else {
        const question = {
          questionText: textInput,
          text: textInput
        };

        this.$emit('chat', {question});
      }

      this.textInput = '';
    },

    onFocus(focused) {
      setTimeout(() => this.$emit('chat-input-focus', focused), 100);
    }
  }
};

export default ChatForm;
</script>

<style lang="sass">
  @import "src/scss/variables"

  .chat-form
    padding: 0

    .input
      flex-wrap: nowrap

      textarea
        width: 100%
        resize: none
        padding: 10px
        outline: none
        font-size: 16px

      .recipient-info
        width: 100%
        font-size: 0.85em
        text-align: right
        margin: 0 5px

    .form-group
      margin-bottom: 0

    .action
      max-height: 40px
      margin-top: 5px

      .col-left
        padding-right: 0

      .col-right
        padding-left: 0
        text-align: right

    .dropdown
      button:focus
        outline: 0 !important

    &.no-other-recipients
      button:first-child
        border-radius: $btn-border-radius-sm !important

      .dropdown-toggle-split
        display: none

    .dropdown-menu
      font-size: .85em

      a
        outline: none

</style>
